import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Chip,
  Stack,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Undo,
  CloudDone,
} from '@mui/icons-material';
import useFileUploadBox from '../hooks/useFileUploadBox';

const truncateFileName = (fileName, maxLength = 30) => {
  if (fileName.length <= maxLength) return fileName;
  const extension = fileName.split('.').pop();
  const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
  const truncated = nameWithoutExt.substring(0, maxLength - extension.length - 3);
  return `${truncated}...${extension}`;
};

/**
 * Componente reutilizable para subir archivos con drag-and-drop
 * Soporta validación de formato, tamaño, cantidad, archivos duplicados y compresión de imágenes
 *
 * @param {Object} props - Props del componente
 * @param {string} props.label - Etiqueta del campo
 * @param {boolean} props.required - Si el campo es requerido
 * @param {Array<Object>} props.files - Array de fileMetadata objects
 * @param {Function} props.onFilesChange - Callback cuando cambian los archivos (recibe archivos comprimidos)
 * @param {Function} props.onRetry - Callback para reintentar subida de archivo
 * @param {number} props.maxFiles - Máximo de archivos permitidos (default: 3)
 * @param {number} props.maxSizeMB - Tamaño máximo total en MB (default: 4)
 * @param {boolean} props.disabled - Si el componente está deshabilitado
 * @param {boolean} props.compressFiles - Si se deben comprimir los archivos (default: true)
 */
const FileUploadBox = ({
  label,
  required = false,
  files = [],
  onFilesChange,
  onRetry,
  onRestore,
  maxFiles = 3,
  maxSizeMB = 4,
  disabled = false,
  compressFiles = true,
  setMessage,
}) => {
  const {
    // Refs
    fileInputRef,

    // Estado
    isDragging,
    errorMessage,
    isCompressing,
    compressionProgress,

    // Métodos
    getFileIconType,
    formatFileSize,
    getTotalSizeMB,
    isMaxFiles,

    // Handlers
    handleButtonClick,
    handleInputChange,
    handleRemoveFile,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  } = useFileUploadBox({
    files,
    onFilesChange,
    maxFiles,
    disabled,
    compressFiles,
    setMessage,
  });

  // Hooks para responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ! IMPORTANTE: Para previsualizar el archivo que se intenta subir
  // Se puede usar URL.createObjectURL(file) para generar una URL temporal
  // y mostrarla en un modal o en una nueva pestaña.
  // Ejemplo: window.open(URL.createObjectURL(file), '_blank');
  // Recordar revocar la URL con URL.revokeObjectURL() cuando ya no se necesite.

  /**
   * Obtiene el componente de icono según el tipo y estado
   */
  const getFileIcon = (fileMetadata) => {
    const file = fileMetadata.file || fileMetadata;
    const iconType = getFileIconType(file);

    let baseIcon;
    switch (iconType) {
      case 'pdf':
        baseIcon = <PdfIcon sx={{ fontSize: 30, color: '#616161ba' }} />;
        break;
      case 'image':
        baseIcon = <ImageIcon sx={{ fontSize: 30, color: '#616161ba' }} />;
        break;
      default:
        baseIcon = <FileIcon sx={{ fontSize: 30, color: '#616161ba' }} />;
    }

    return baseIcon;
  };

  const totalSizeMB = getTotalSizeMB();
  const hasError = !!errorMessage;
  const showRequiredError = required && files.length === 0 && !hasError;

  return (
    <>
      <Card
        elevation={0}
        variant='outlined'
        sx={{
          border: '1px solid',
          borderColor: hasError || showRequiredError ? 'error.main' : 'divider',
          borderRadius: 2,
          boxShadow: 'none',
        }}
      >
        <CardContent>
          {/* Header */}
          <Box sx={{ mb: 2 }}>
            <Typography variant='subtitle1' fontWeight='600' gutterBottom>
              {label}
              {required && (
                <Typography component='span' color='error.main'>
                  {' '}
                  *
                </Typography>
              )}
            </Typography>
            {/* <Typography variant="caption" color="text.secondary">
              Formatos: PDF, JPG, JPEG | Máx {maxFiles} archivos | Tamaño máx: {maxSizeMB}MB
            </Typography> */}
          </Box>

          {/* Área de drag & drop (Solo Desktop) */}
          {!isMobile && (
            <Box
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: isDragging
                  ? 'primary.main'
                  : hasError || showRequiredError
                  ? 'error.light'
                  : 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                backgroundColor: isDragging ? alpha('#1976d2', 0.05) : 'background.paper',
                cursor: disabled || isMaxFiles || isCompressing ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                mb: files.length > 0 ? 2 : 0,
                '&:hover': !disabled &&
                  !isMaxFiles &&
                  !isCompressing && {
                    borderColor: 'primary.main',
                    backgroundColor: alpha('#1976d2', 0.02),
                  },
              }}
              onClick={handleButtonClick}
            >
              <CloudUploadIcon
                sx={{
                  fontSize: 48,
                  color: isDragging
                    ? 'primary.main'
                    : disabled || isMaxFiles || isCompressing
                    ? 'text.disabled'
                    : 'action.active',
                  mb: 1,
                }}
              />

              <Typography variant='body1' gutterBottom fontWeight='500'>
                {isCompressing
                  ? 'Comprimiendo archivos...'
                  : isDragging
                  ? 'Suelta los archivos aquí'
                  : isMaxFiles
                  ? `Máximo de ${maxFiles} archivos alcanzado`
                  : 'Arrastra archivos aquí o haz clic para seleccionar'}
              </Typography>

              <Button
                variant='contained'
                startIcon={<CloudUploadIcon />}
                disabled={disabled || isMaxFiles || isCompressing}
                sx={{ mt: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleButtonClick();
                }}
              >
                {isCompressing ? 'Comprimiendo...' : 'Seleccionar archivos'}
              </Button>
            </Box>
          )}

          {/* Input oculto siempre presente */}
          <input
            ref={fileInputRef}
            type='file'
            accept='.pdf,.jpg,.jpeg,image/jpeg,image/jpg,application/pdf'
            multiple
            onChange={handleInputChange}
            style={{ display: 'none' }}
            disabled={disabled || isMaxFiles}
          />

          {/* Progreso de compresión */}
          {isCompressing && compressionProgress !== null && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant='caption' color='primary'>
                  Comprimiendo archivos...
                </Typography>
                <Typography variant='caption' fontWeight='600' color='primary'>
                  {Math.round(compressionProgress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant='determinate'
                value={compressionProgress}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          )}

          {/* Lista de archivos */}
          {files.length > 0 && (
            <Box>
              <Stack spacing={1}>
                {files.map((fileMetadata, index) => {
                  const file = fileMetadata.file || fileMetadata;
                  const status = fileMetadata.status || 'uploaded';
                  const progress = fileMetadata.uploadProgress || 0;
                  const error = fileMetadata.error;

                  // Obtener propiedades de manera segura (para archivos existing/pending sin File object)
                  const fileName = fileMetadata.fileName || file.name || 'archivo.pdf';
                  const fileSize = fileMetadata.size || file.size || 0;
                  const fileType =
                    file.type ||
                    (fileName.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg');
                  const isDeleted = status === 'deleted';
                  const uploadedAt = fileMetadata.uploadedAt
                    ? new Date(fileMetadata.uploadedAt)
                    : null;

                  return (
                    <Card
                      elevation={0}
                      key={`${fileName}-${fileSize}-${index}`}
                      variant='outlined'
                      sx={{
                        p: 1.5,
                        backgroundColor: isDeleted ? alpha('#f44336', 0.05) : 'background.default',
                        boxShadow: 'none',
                        // borderColor: isDeleted ? 'error.light' : 'divider',
                        // opacity: isDeleted ? 0.6 : 1,
                      }}
                    >
                      {/* Contenedor principal */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* Icono del archivo */}
                        <Box sx={{ mr: 2 }}>{getFileIcon(fileMetadata)}</Box>

                        {/* Información del archivo */}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography
                              variant='body2'
                              fontWeight='500'
                              noWrap
                              sx={{
                                flexGrow: 1,
                                textDecoration: isDeleted ? 'line-through' : 'none',
                              }}
                              title={fileName}
                            >
                              {truncateFileName(fileName, isMobile ? 25 : 50)}
                            </Typography>
                          </Box>
                          {uploadedAt && (
                            <Typography
                              variant='caption'
                              color='text.disabled'
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 0.5,
                                fontSize: '0.7rem',
                                letterSpacing: 0.1,
                              }}
                            >
                              <CloudDone
                                sx={{ mr: 0.5, fontSize: '0.8rem', verticalAlign: 'middle' }}
                                color='success'
                              />
                              {'Subido el '}
                              {uploadedAt.toLocaleDateString('es-AR', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })}
                              {' · '}
                              {uploadedAt.toLocaleTimeString('es-AR', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                          )}

                          <Box
                            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
                          >
                            <Chip
                              label={formatFileSize(fileSize)}
                              size='small'
                              color='default'
                              variant='filled'
                            />
                            {/* <Chip
                              label={fileType.includes('pdf') || fileName.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Imagen'}
                              size="small"
                              color="default"
                              variant="filled"
                            /> */}

                            {/* Estado del archivo */}
                            {status === 'uploading' && (
                              <Chip
                                label='Subiendo...'
                                size='small'
                                color='info'
                                variant='filled'
                              />
                            )}
                            {status === 'uploaded' && (
                              <Chip
                                label='Subido'
                                size='small'
                                color='success'
                                variant='filled'
                                icon={<CheckCircleIcon fontSize='small' />}
                              />
                            )}
                            {status === 'existing' && (
                              <Chip
                                label='Existente'
                                size='small'
                                color='success'
                                variant='outlined'
                                icon={<CheckCircleIcon fontSize='small' />}
                              />
                            )}
                            {status === 'deleted' && (
                              <Chip
                                label='Eliminado'
                                size='small'
                                color='error'
                                variant='filled'
                                icon={<ErrorIcon fontSize='small' />}
                              />
                            )}
                            {status === 'error' && (
                              <Chip
                                label='Error'
                                size='small'
                                color='error'
                                variant='filled'
                                icon={<ErrorIcon fontSize='small' />}
                              />
                            )}
                            {status === 'pending' && (
                              <Chip
                                label='Pendiente'
                                size='small'
                                color='warning'
                                variant='outlined'
                              />
                            )}
                          </Box>

                          {/* Mensaje de error */}
                          {status === 'error' && error && (
                            <Typography
                              variant='caption'
                              color='error'
                              sx={{ mt: 0.5, display: 'block' }}
                            >
                              {error}
                            </Typography>
                          )}

                          {/* Mensaje para deleted */}
                          {status === 'deleted' && (
                            <Typography
                              variant='caption'
                              color='error'
                              sx={{ mt: 0.5, display: 'block' }}
                            >
                              Será eliminado al enviar el formulario. Puede deshacerlo.
                            </Typography>
                          )}
                        </Box>

                        {/* Botón Deshacer (solo para deleted) */}
                        {status === 'deleted' && onRestore && (
                          <IconButton
                            onClick={() => onRestore(index)}
                            color='primary'
                            size='small'
                            sx={{ ml: 1 }}
                            title='Deshacer eliminación'
                            aria-label='Deshacer eliminación'
                          >
                            <Undo />
                          </IconButton>
                        )}

                        {/* Botón reintentar (solo para errores de UPLOAD) */}
                        {status === 'error' && !fileMetadata.errorType && onRetry && (
                          <IconButton
                            onClick={() => onRetry(index)}
                            color='secondary'
                            size='small'
                            sx={{ ml: 1 }}
                            aria-label='Reintentar subida'
                          >
                            <RefreshIcon />
                          </IconButton>
                        )}

                        {/* Botón eliminar (NO mostrar para deleted) */}
                        {status !== 'deleted' && (
                          <IconButton
                            onClick={() => handleRemoveFile(index)}
                            disabled={disabled || status === 'uploading'}
                            color='primary'
                            size='small'
                            sx={{ ml: 1 }}
                            aria-label={
                              fileMetadata.errorType === 'delete'
                                ? 'Reintentar eliminación'
                                : `Eliminar ${fileName}`
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>

                      {/* Barra de progreso para archivos en upload */}
                      {status === 'uploading' && progress > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant='caption' color='text.secondary'>
                              Subiendo...
                            </Typography>
                            <Typography variant='caption' fontWeight='600' color='primary'>
                              {Math.round(progress)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant='determinate'
                            value={progress}
                            sx={{ height: 4, borderRadius: 2 }}
                          />
                        </Box>
                      )}
                    </Card>
                  );
                })}
              </Stack>

              {/* Información de tamaño total */}
              <Box sx={{ mt: 2 }}>
                <Typography variant='caption' fontWeight='600'>
                  Archivos adjuntados ({files.length}/{maxFiles})
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Tamaño total
                  </Typography>
                  <Typography
                    variant='caption'
                    fontWeight='600'
                    color={totalSizeMB > maxSizeMB ? 'error.main' : 'text.secondary'}
                  >
                    {totalSizeMB.toFixed(2)} MB / {maxSizeMB} MB
                  </Typography>
                </Box>
                <LinearProgress
                  variant='determinate'
                  value={Math.min((totalSizeMB / maxSizeMB) * 100, 100)}
                  color={totalSizeMB > maxSizeMB ? 'error' : 'primary'}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            </Box>
          )}

          {/* Botón de subida para Mobile (después de la lista) */}
          {isMobile && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant='outlined'
                startIcon={<CloudUploadIcon />}
                fullWidth
                disabled={disabled || isMaxFiles || isCompressing}
                onClick={handleButtonClick}
                sx={{ py: 1.5, borderStyle: 'dashed', borderWidth: 2 }}
              >
                {isCompressing
                  ? 'Comprimiendo...'
                  : isMaxFiles
                  ? 'Límite alcanzado'
                  : 'Seleccionar archivos'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default FileUploadBox;
